import PropTypes from 'prop-types';
import { Fragment } from 'react';

import CmsBlock from 'Component/CmsBlock';
import ContentWrapper from 'Component/ContentWrapper';
import CustomerService from 'Component/CustomerService';
import ExpandableContent from 'Component/ExpandableContent';
import Link from 'Component/Link';
import NewsletterSubscription from 'Component/NewsletterSubscription';
import PaymentMethods from 'Component/PaymentMethods';
import StoreSwitcher from 'Component/StoreSwitcher';
import WidgetContentLocation from 'Component/WidgetContentLocation';
import { GROUP_ID_PRO } from 'Route/MyAccount/MyAccount.config';
import {
    Footer as SourceFooter
} from 'SourceComponent/Footer/Footer.component';

import {
    COLUMN_MAP,
    NEWSLETTER_COLUMN,
    RENDER_ABOUT,
    RENDER_HELP,
    RENDER_NEWSLETTER,
    RENDER_SERVICES
} from './Footer.config';

import './Footer.override.style';

/** @namespace PeggysagePwa/Component/Footer/Component */
export class FooterComponent extends SourceFooter {
    static propTypes = {
        ...SourceFooter.propTypes,
        isEmbed: PropTypes.bool.isRequired,
        isCheckout: PropTypes.bool.isRequired,
        userGroupId: PropTypes.number.isRequired
    };

    renderMap = {
        ...this.renderMap,
        [RENDER_NEWSLETTER]: {
            render: this.renderNewsletterSubscriptionBlock.bind(this)
        },
        [RENDER_ABOUT]: {
            render: this.renderAboutBlock.bind(this)
        },
        [RENDER_HELP]: {
            render: this.renderHelpBlock.bind(this)
        },
        [RENDER_SERVICES]: {
            render: this.renderServicesBlock.bind(this)
        }
    };

    /**
     * Override: update component when location change from or to checkout pages
     * @param nextProps
     * @returns {boolean|boolean}
     */
    // eslint-disable-next-line @scandipwa/scandipwa-guidelines/only-render-in-component
    shouldComponentUpdate(nextProps) {
        const { isCheckout, userGroupId } = this.props;
        const { isCheckout: nextIsCheckout, nextUserGroupId } = nextProps;

        return isCheckout !== nextIsCheckout
            || userGroupId !== nextUserGroupId
            || super.shouldComponentUpdate(nextProps);
    }

    renderByGardeners() {
        return (
            <div block="Footer" elem="ByGrdnrs">
                <Link
                  block="Footer"
                  elem="ByGrdnrsLink"
                  to="https://www.agencegardeners.com"
                  aria-label={ __('Agence Gardeners') }
                >
                    <span className="by-grdnrs">{ __('By Gardeners') }</span>
                </Link>
            </div>
        );
    }

    renderBottom() {
        return (
            <div block="Footer" elem="Bottom">
                <ContentWrapper
                  isNotSection
                  mix={ { block: 'Footer', elem: 'BottomContentWrapper' } }
                  wrapperMix={ { block: 'Footer', elem: 'BottomContent' } }
                  label=""
                >
                    { this.renderByGardeners() }
                </ContentWrapper>
            </div>
        );
    }

    renderReinsuranceBlock() {
        const { userGroupId } = this.props;

        const identifier = userGroupId === GROUP_ID_PRO ? 'footer_reinsurance_pro' : 'footer_reinsurance';

        return (
            <div block="Footer" elem="Reinsurance">
                <CmsBlock identifier={ identifier } />
            </div>
        );
    }

    /**
     * Add description to newsletter block
     * @returns {JSX.Element}
     */
    renderNewsletterSubscriptionBlock() {
        const { isEmbed } = this.props;

        return (
            <Fragment key="NewsletterSubscription">
                { !isEmbed && (
                    <>
                        <p>
                            { __('Inscrivez-vous et recevez des conseils, des tutos, suivez les actualités et les offres de '
                                + 'Peggy Sage') }
                        </p>
                        <NewsletterSubscription />
                    </>
                ) }
                { this.renderSocialLinksBlock() }
            </Fragment>
        );
    }

    renderAboutBlock() {
        return <CmsBlock identifier="footer_about_us" key="AboutBlock" />;
    }

    renderHelpBlock() {
        return <CmsBlock identifier="footer_help" key="HelpBlock" />;
    }

    renderServicesBlock() {
        return <CmsBlock identifier="footer_services" key="ServicesBlock" />;
    }

    renderSocialLinksBlock() {
        // @TODO replace by custom block with store config values ?
        // (in General > social_information > instagram_url etc.)

        return (
            <div block="Footer" elem="SocLinks">
                <CmsBlock identifier="soc_links" />
            </div>
        );
    }

    renderPaymentMethodsBlock() {
        return <PaymentMethods />;
    }

    renderCustomerServiceBlock(title = null) {
        const props = {};

        if (title) {
            props.title = title;
        }

        // eslint-disable-next-line @scandipwa/scandipwa-guidelines/jsx-no-props-destruction
        return <CustomerService { ...props } />;
    }

    renderStoreSwitcher() {
        return (
            <div block="Footer" elem="StoreSwitcher">
                <StoreSwitcher />
            </div>
        );
    }

    renderColumnNewsletter(column) {
        const {
            title,
            columnActiveKey,
            items,
            isItemsHorizontal,
            mods = {}
        } = column;

        const contentMods = isItemsHorizontal ? { direction: 'horizontal' } : {};

        const { [columnActiveKey]: isColumnActive, isEmbed } = this.props;

        if (columnActiveKey && !isColumnActive === true) {
            return null;
        }

        return (
            <div block="Footer" elem="Column" mods={ { ...mods, isEmbed } } className="Footer-Newsletter">
                <h3 block="Footer" elem="ColumnTitle">
                    { title }
                </h3>
                <div
                  block="Footer"
                  elem="ColumnContent"
                  mods={ contentMods }
                >
                    { items.map(this.renderColumnItem.bind(this)) }
                </div>
            </div>
        );
    }

    /**
     * Add expandable content on mobile
     * @param column
     * @param i
     * @param isExpandable
     * @returns {JSX.Element|null}
     */
    renderColumn(column, i) {
        const {
            title,
            columnActiveKey,
            items,
            isItemsHorizontal,
            mods = {}
        } = column;

        const contentMods = isItemsHorizontal ? { direction: 'horizontal' } : {};

        const { [columnActiveKey]: isColumnActive, device: { isMobile }, isEmbed } = this.props;

        if (columnActiveKey && !isColumnActive === true) {
            return null;
        }

        if (isMobile) {
            return (
                <div block="Footer" elem="Column" mods={ mods } key={ i }>
                    <ExpandableContent
                      mix={ { block: 'Footer', elem: 'ColumnContent' } }
                      heading={ title }
                    >
                        { items.map(this.renderColumnItem.bind(this)) }
                    </ExpandableContent>
                </div>
            );
        }

        return (
            <div block="Footer" elem="Column" mods={ { ...mods, isEmbed } } key={ i }>
                <h3 block="Footer" elem="ColumnTitle">
                    { title }
                </h3>
                <div
                  block="Footer"
                  elem="ColumnContent"
                  mods={ contentMods }
                >
                    { items.map(this.renderColumnItem.bind(this)) }
                </div>
            </div>
        );
    }

    /**
     * Add parameter to render newsletter block inside columns
     * @returns {JSX.Element}
     */
    renderColumns(withNewsletterBlock = false) {
        const [head, ...tail] = COLUMN_MAP;
        const columnsArr = withNewsletterBlock ? [NEWSLETTER_COLUMN, head, ...tail] : [head, ...tail];

        return (
            <ContentWrapper
              isNotSection
              wrapperMix={ { block: 'Footer', elem: 'Columns' } }
              label=""
            >
                { columnsArr.map(this.renderColumn.bind(this)) }
            </ContentWrapper>
        );
    }

    renderContentMobile() {
        const { isCheckout } = this.props;

        if (isCheckout) {
            return (
                <>
                    <div block="Footer" elem="Blocks">
                        <ContentWrapper
                          isNotSection
                          mix={ { block: 'Footer', elem: 'BlocksContentWrapper' } }
                          wrapperMix={ { block: 'Footer', elem: 'BlocksContent' } }
                          label=""
                        >
                            { this.renderCustomerServiceBlock(__('Contactez notre service client')) }
                            { this.renderPaymentMethodsBlock() }
                        </ContentWrapper>
                    </div>
                    { this.renderReinsuranceBlock() }
                </>
            );
        }

        return (
            <>
                { this.renderReinsuranceBlock() }
                { this.renderColumnNewsletter(NEWSLETTER_COLUMN) }
                <div block="Footer" elem="Blocks">
                    <ContentWrapper
                      isNotSection
                      mix={ { block: 'Footer', elem: 'BlocksContentWrapper' } }
                      wrapperMix={ { block: 'Footer', elem: 'BlocksContent' } }
                      label=""
                    >
                        { this.renderPaymentMethodsBlock() }
                        { this.renderCustomerServiceBlock() }
                    </ContentWrapper>
                </div>
                <div block="Footer" elem="Content">
                    { this.renderColumns(false) }
                    { this.renderStoreSwitcher() }
                    { this.renderByGardeners() }
                </div>
            </>
        );
    }

    renderContentDesktop() {
        const { isCheckout } = this.props;

        if (isCheckout) {
            return this.renderReinsuranceBlock();
        }

        return (
            <>
                { this.renderReinsuranceBlock() }
                <div block="Footer" elem="Content">
                    { this.renderColumns(true) }
                </div>
                <div block="Footer" elem="Blocks">
                    <ContentWrapper
                      isNotSection
                      mix={ { block: 'Footer', elem: 'BlocksContentWrapper' } }
                      wrapperMix={ { block: 'Footer', elem: 'BlocksContent' } }
                      label=""
                    >
                        { this.renderCustomerServiceBlock() }
                        { this.renderPaymentMethodsBlock() }
                        { this.renderStoreSwitcher() }
                    </ContentWrapper>
                </div>
                { this.renderBottom() }
            </>
        );
    }

    /**
     * Override
     * @returns {*}
     */
    renderContent() {
        const { device: { isMobile } } = this.props;

        if (isMobile) {
            return this.renderContentMobile();
        }

        return this.renderContentDesktop();
    }

    renderWidgetLocation() {
        const { isCheckout } = this.props;

        if (isCheckout) {
            return null;
        }

        return (
            <ContentWrapper
              label=""
              mix={ { block: 'FooterWidget' } }
              wrapperMix={ { block: 'FooterWidget', elem: 'Wrapper' } }
            >
                <WidgetContentLocation container="pwa-before-footer-container" />
            </ContentWrapper>
        );
    }

    /**
     * Override: Rearrange footer display & blocks
     * @returns {JSX.Element|null}
     */
    render() {
        const { isVisibleOnMobile, device: { isMobile }, isCheckout } = this.props;

        if (!isVisibleOnMobile && isMobile) {
            return null;
        }

        return (
            <footer block="Footer" aria-label="Footer" mods={ { isCheckout } }>
                { this.renderWidgetLocation() }
                { this.renderContent() }
            </footer>
        );
    }
}

export default FooterComponent;
